import clsx from 'clsx';
import React, { FC, ReactElement } from 'react';

import InfoIconBox, { InfoIconBoxProps } from '../infoIconBox';

export interface RadioButtonProps {
  id?: string;
  label?: string | ReactElement;
  name?: string;
  value?: string;
  checked?: boolean;
  onChange?: (value: string) => void;
  disabled?: boolean;
  invalid?: boolean;
  className?: string;
  darkBg?: boolean;
  infoIcon?: InfoIconBoxProps;
  preventLabelClick?: boolean;
  formik?: any;
}

const RadioButton: FC<RadioButtonProps> = (props: RadioButtonProps) => {
  const {
    id,
    label,
    name,
    value,
    checked = false,
    onChange = () => null,
    disabled = false,
    invalid = false,
    className: customClassName,
    darkBg = false,
    infoIcon,
    preventLabelClick = false,
    formik,
  } = props;

  const isChecked = formik?.values[name as string] === value;

  const className = {
    wrapper: clsx('align-top inline-flex items-center', customClassName),
    label: clsx(
      'flex items-start text-p1',
      !disabled && 'cursor-pointer',
      !disabled && darkBg && !invalid && 'text-white',
      !disabled &&
        !darkBg &&
        !invalid &&
        'text-purple80 monochrome:text-grey90',
      disabled && !invalid && 'text-lightPurple monochrome:text-grey60',
      invalid && 'text-red60'
    ),
    input: clsx(
      'appearance-none border-1px grow-0 h-20px rounded-circle shrink-0 w-20px checked:bg-gradient-radial',
      !!label && 'mr-10px',
      !disabled && 'cursor-pointer',
      !disabled && !checked && 'hover:border-3px',
      !disabled && darkBg && !invalid && 'from-white to-white border-white',
      !disabled &&
        !darkBg &&
        !invalid &&
        'from-purple80 to-purple80 monochrome:from-grey90 monochrome:to-grey90 border-purple80 monochrome:border-grey90',
      disabled &&
        !invalid &&
        'border-lightPurple from-lightPurple to-lightPurple monochrome:border-grey60 monochrome:from-grey60 monochrome:to-grey60',
      invalid && 'border-red60 from-red60 to-red60',
      'group-hover:border-3px group-hover:outline-purple80',
      'monochrome:group-hover:outline-grey90'
    ),
    infoIcon: 'ml-20px flex justify-center',
  };

  return (
    <span className={className.wrapper}>
      <label
        className={className.label}
        onClick={(e) => (preventLabelClick ? e.preventDefault() : null)}
      >
        <input
          id={`${id}--input`}
          data-tracking-id={`${id}--input`}
          type="radio"
          className={className.input}
          name={name}
          value={value}
          disabled={disabled}
          checked={formik ? isChecked : checked}
          onChange={(e) => {
            onChange(e.target.value);
            formik?.handleChange(e);
          }}
        />
        {typeof label === 'string' ? (
          <span className="whitespace-pre-wrap">{label}</span>
        ) : (
          label
        )}
      </label>
      {infoIcon && (
        <span className={className.infoIcon}>
          <InfoIconBox
            text={infoIcon.text}
            darkBg={darkBg}
            disabled={disabled}
            invalid={invalid}
          />
        </span>
      )}
    </span>
  );
};

export default RadioButton;
